/**
 * SigningCeremonyPage — full-screen landlord in-app signing (`/signing/:id/sign`).
 *
 * The single place the landlord signs in-app (BR-DS-02 / BR-DS-13 / BR-LA-20): the envelope detail
 * page's Sign button, the amendment page's "Sign now", and the lease in-flight card's "Sign" all
 * navigate here. On mount it fetches the manager signing token, shows the shared ESIGN consent
 * (consent is legally required and is never skipped), then the embedded ceremony at the full width
 * of the viewport (no AppShell, no max-width gutter).
 *
 * The signing token is held ONLY in component state — never in the URL, router state, localStorage
 * or sessionStorage (BR-DS-13). Exit/completion returns to `location.state.returnTo` (the launching
 * page) or the envelope detail page.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getManagerSigningToken, getSigningEnvelope, recordCeremonyConsent } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { SigningCeremony } from '../../components/signing/SigningCeremony';
import { EsignConsent } from '../../components/signing/EsignConsent';
import {
  safeReturnTo,
  signingEnvelopePath,
  type SigningLaunchState,
  type SigningReturnState,
} from '../../utils/signing-navigation';

type Phase = 'loading' | 'consent' | 'ceremony' | 'error';

export function SigningCeremonyPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const returnTo = safeReturnTo((location.state as SigningLaunchState | null)?.returnTo, signingEnvelopePath(id ?? ''));

  const [phase, setPhase] = useState<Phase>('loading');
  const [signingToken, setSigningToken] = useState<string | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConsenting, setIsConsenting] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Fetch the signing token (and, best effort, the envelope title for the header).
  useEffect(() => {
    if (!id) return;
    let ignore = false;
    setPhase('loading');
    setError(null);
    setSigningToken(null);
    getManagerSigningToken(id)
      .then((res) => {
        if (ignore) return;
        setSigningToken(res.signingToken);
        setPhase('consent');
      })
      .catch((err: unknown) => {
        if (ignore) return;
        setError(err instanceof Error ? err.message : 'Failed to start signing');
        setPhase('error');
      });
    getSigningEnvelope(id)
      .then((env) => {
        if (!ignore) setTitle((env as { title?: string }).title ?? null);
      })
      .catch(() => {
        /* the title is decorative — never block signing on it */
      });
    return () => {
      ignore = true;
    };
  }, [id, attempt]);

  // Move focus to the new region on each phase change (screen readers / keyboard users).
  useEffect(() => {
    if (phase !== 'loading') headingRef.current?.focus();
  }, [phase]);

  const leave = useCallback(
    (signingResult: SigningReturnState['signingResult']) => {
      const state: SigningReturnState = { signingResult };
      navigate(returnTo, { replace: true, state });
    },
    [navigate, returnTo],
  );

  async function handleAgreeConsent() {
    if (!signingToken) return;
    try {
      setIsConsenting(true);
      setError(null);
      await recordCeremonyConsent(signingToken);
      setPhase('ceremony');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record consent');
    } finally {
      setIsConsenting(false);
    }
  }

  const handleCeremonyComplete = useCallback(() => leave('completed'), [leave]);
  const handleCeremonyError = useCallback((message: string) => setError(message), []);

  const announcement =
    phase === 'consent'
      ? 'Electronic signature consent form opened.'
      : phase === 'ceremony'
        ? 'Signing ceremony started.'
        : '';

  return (
    <div
      className="min-h-screen bg-muted/30 flex flex-col"
      data-testid="signing-ceremony-page"
      // Full-screen route: there is no bottom tab bar, so the ceremony's mobile action bar sits flush
      // with the bottom edge instead of floating above the app shell's tab-bar height.
      style={{ ['--bottom-tab-height' as string]: '0px' }}
    >
      <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-border bg-card px-3 sm:px-6 py-2 min-h-[56px]">
        <Button
          type="button"
          variant="outline"
          data-testid="signing-detail-exit-signing-btn"
          onClick={() => leave('exited')}
          disabled={isConsenting}
          className="min-h-[44px] shrink-0"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" aria-hidden="true" />
          Exit signing
        </Button>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="m-0 min-w-0 truncate text-base font-semibold text-foreground outline-none"
          data-testid="signing-ceremony-page-title"
        >
          {title ? `Sign: ${title}` : 'Sign document'}
        </h1>
      </header>

      <div aria-live="polite" className="sr-only">
        {announcement}
      </div>

      <main className="flex-1 w-full px-3 sm:px-6 py-4">
        {phase === 'loading' && (
          <div
            role="status"
            aria-live="polite"
            data-testid="signing-ceremony-page-loading"
            className="flex flex-col items-center justify-center py-24 text-sm text-muted-foreground"
          >
            <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin mb-4" aria-hidden="true" />
            Preparing your signing session...
          </div>
        )}

        {error && phase !== 'loading' && (
          <div
            role="alert"
            data-testid="signing-ceremony-page-error"
            className="mx-auto mb-4 max-w-3xl rounded-md border border-destructive/25 bg-destructive/10 p-3 text-sm text-destructive"
          >
            {error}
          </div>
        )}

        {phase === 'error' && (
          <div className="mx-auto flex max-w-3xl flex-wrap gap-3">
            <Button type="button" data-testid="signing-ceremony-page-retry-btn" onClick={() => setAttempt((n) => n + 1)} className="min-h-[44px]">
              Try again
            </Button>
            <Button type="button" variant="outline" onClick={() => leave('exited')} className="min-h-[44px]">
              Go back
            </Button>
          </div>
        )}

        {/* Consent is legally required before the ceremony (BR-DS-02): same shared EsignConsent
            as the public signing page. Readable width; the ceremony below is full width. */}
        {phase === 'consent' && (
          <div
            className="mx-auto max-w-3xl rounded-lg border border-border bg-card p-6"
            data-testid="signing-detail-consent"
          >
            <h2 className="mb-4 mt-0 text-lg font-semibold text-foreground">Electronic Signature Consent</h2>
            <EsignConsent
              onAgree={handleAgreeConsent}
              agreeTestId="signing-detail-consent-agree-btn"
              agreeAriaLabel="I agree, proceed to sign"
              agreePending={isConsenting}
              agreeDisabled={isConsenting || !signingToken}
              agreeButtonClassName="min-h-[44px]"
              secondaryAction={
                <Button
                  type="button"
                  data-testid="signing-detail-consent-cancel-btn"
                  onClick={() => leave('exited')}
                  disabled={isConsenting}
                  variant="outline"
                  className="min-h-[44px]"
                >
                  Cancel
                </Button>
              }
            />
          </div>
        )}

        {phase === 'ceremony' && signingToken && (
          <section aria-label="Signing ceremony" className="w-full" data-testid="signing-detail-ceremony">
            <SigningCeremony
              signingToken={signingToken}
              onComplete={handleCeremonyComplete}
              onError={handleCeremonyError}
            />
          </section>
        )}
      </main>
    </div>
  );
}

export default SigningCeremonyPage;
